import { Injectable } from '@angular/core';
import * as WavEncoder from 'wav-encoder';

@Injectable({
  providedIn: 'root'
})
export class AudioService {
  private audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

  public async decodeAudioData(blob: Blob): Promise<AudioBuffer> {
    try {
      console.log('Decoding audio data...');
      const arrayBuffer = await blob.arrayBuffer();
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      console.log('Audio data decoded successfully');
      return audioBuffer;
    } catch (error) {
      console.error('Error decoding audio data:', error);
      throw error;
    }
  }

  private convertToStereo(buffer: AudioBuffer): AudioBuffer {
    try {
      console.log('Converting audio buffer to stereo...');
      if (buffer.numberOfChannels === 2) {
        console.log('Audio buffer is already stereo');
        return buffer;
      }

      const stereoBuffer = this.audioContext.createBuffer(2, buffer.length, buffer.sampleRate);
      const monoData = buffer.getChannelData(0);
      stereoBuffer.copyToChannel(monoData, 0);
      stereoBuffer.copyToChannel(monoData, 1);
      console.log('Audio buffer converted to stereo successfully');
      return stereoBuffer;
    } catch (error) {
      console.error('Error converting audio buffer to stereo:', error);
      throw error;
    }
  }

  public concatenateAudioBuffers(audioBuffers: AudioBuffer[]): AudioBuffer {
    try {
      console.log('Concatenating audio buffers...');
      const stereoBuffers = audioBuffers.map(buffer => this.convertToStereo(buffer));
      const length = stereoBuffers.reduce((sum, buffer) => sum + buffer.length, 0);
      const sampleRate = stereoBuffers[0].sampleRate;

      const concatenatedBuffer = this.audioContext.createBuffer(2, length, sampleRate);

      let offset = 0;
      for (const buffer of stereoBuffers) {
        concatenatedBuffer.copyToChannel(buffer.getChannelData(0), 0, offset);
        concatenatedBuffer.copyToChannel(buffer.getChannelData(1), 1, offset);
        offset += buffer.length;
      }
      console.log('Audio buffers concatenated successfully');
      return concatenatedBuffer;
    } catch (error) {
      console.error('Error concatenating audio buffers:', error);
      throw error;
    }
  }

  public async encodeAudioBufferToBlob(buffer: AudioBuffer): Promise<Blob> {
    try {
      console.log('Encoding audio buffer to blob...');
      const wavArrayBuffer = await WavEncoder.encode({
        sampleRate: buffer.sampleRate,
        channelData: Array.from({ length: buffer.numberOfChannels }, (_, i) => buffer.getChannelData(i))
      });
      const blob = new Blob([wavArrayBuffer], { type: 'audio/wav' });
      console.log('Audio buffer encoded to blob successfully');
      return blob;
    } catch (error) {
      console.error('Error encoding audio buffer to blob:', error);
      throw error;
    }
  }
}
